/**
 * InlineSparkline - Tiny word-sized sparkline chart (Tufte principle).
 *
 * Pure data ink: no axes, no grid, no tooltip. Just the line.
 * Sport-agnostic: accepts any numeric data array.
 * Auto-zooms Y domain to show relative variation (not absolute).
 *
 * @param {number[]} data - Array of numeric values
 * @param {number} width - Chart width in pixels (default 60)
 * @param {number} height - Chart height in pixels (default 16)
 * @param {string} color - Line color (default accent-blue)
 * @param {number} referenceLine - Optional dashed reference line (e.g., season avg)
 * @param {string} dotColor - Optional end-dot color to highlight current value
 */
import { LineChart, Line, ReferenceLine, YAxis, ResponsiveContainer } from 'recharts';

const InlineSparkline = ({
  data = [],
  width = 60,
  height = 16,
  color = '#00D9FF',
  referenceLine = null,
  dotColor = null,
}) => {
  if (!data || data.length < 2) return null;

  const chartData = data.map((value, index) => ({ v: value, i: index }));

  // Calculate tight Y domain to amplify variation
  const values = data.filter(v => v != null);
  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);
  const range = maxVal - minVal;
  // Add 15% padding but ensure minimum range so flat data still shows a line
  const pad = Math.max(range * 0.15, 2);
  const yDomain = [Math.floor(minVal - pad), Math.ceil(maxVal + pad)];

  // Custom dot: only show on the last data point
  const renderDot = (props) => {
    const { cx, cy, index } = props;
    if (index !== chartData.length - 1 || !dotColor) return null;
    return (
      <circle
        cx={cx}
        cy={cy}
        r={2.5}
        fill={dotColor}
        stroke="none"
      />
    );
  };

  return (
    <div style={{ width, height }} className="inline-block align-middle">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 2, right: 3, bottom: 2, left: 3 }}>
          <YAxis domain={yDomain} hide />
          {referenceLine !== null && (
            <ReferenceLine
              y={referenceLine}
              stroke="#666"
              strokeDasharray="2 2"
              strokeWidth={0.5}
            />
          )}
          <Line
            type="monotone"
            dataKey="v"
            stroke={color}
            strokeWidth={1.5}
            dot={renderDot}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default InlineSparkline;
